class ScrollDirectionTracker {
  constructor(options = {}) {
    this.scrollContainerSelector = options.scrollContainerSelector || null;
    this.scrollContainer = options.scrollContainer || null;
    this.threshold = options.threshold != null ? options.threshold : 8;
    this.topOffset = options.topOffset != null ? options.topOffset : 20;
    this.bottomOffset = options.bottomOffset != null ? options.bottomOffset : 4;
    this.minScrollableHeight =
      options.minScrollableHeight != null ? options.minScrollableHeight : 10;
    this.hideableSelector = options.hideableSelector || null;
    this.minScrollableRatio =
      options.minScrollableRatio != null ? options.minScrollableRatio : 0;
    this.scrollRoomTolerance =
      options.scrollRoomTolerance != null ? options.scrollRoomTolerance : 4;
    this.onChange = options.onChange || (() => {});
    this.onScrolledChange = options.onScrolledChange || (() => {});
    this.onScrollableChange = options.onScrollableChange || (() => {});
    this.onScrollRoomChange = options.onScrollRoomChange || (() => {});

    this.isHidden = false;
    this.hasScrolled = false;
    this.scrollable = false;
    this.hasScrollRoom = false;
    this.lastScrollY = 0;
    this.lastFlipTime = 0;
    this.ticking = false;
    this.target = null;
    this.rafId = null;
    this.attachAttempts = 0;
    this.running = false;
    this.resizeObserver = null;
    this.mutationObserver = null;
    this.listening = false;

    this._onScroll = this._onScroll.bind(this);
    this._update = this._update.bind(this);
    this._tryAttach = this._tryAttach.bind(this);
  }

  start() {
    if (this.running) return;
    this.running = true;
    this._tryAttach();
  }

  refresh() {
    if (!this.running || !this.target) return;
    this._update();
  }

  ensureAttached() {
    if (!this.running || this.target) return;
    this.attachAttempts = 0;
    this._tryAttach();
  }

  stop() {
    if (!this.running) return;
    this.running = false;
    this._detach();
    this._setScrollable(false);
    this._setScrollRoom(false);
    if (this.isHidden) {
      this.isHidden = false;
      this.lastFlipTime = 0;
      this.onChange(false);
    }
  }

  _setHidden(next, force) {
    if (next === this.isHidden) return;
    const now = Date.now();
    if (!force && now - this.lastFlipTime < 100) return;
    this.isHidden = next;
    this.lastFlipTime = now;
    this.onChange(next);
  }

  _setScrollable(next) {
    if (next === this.scrollable) return;
    this.scrollable = next;
    this.onScrollableChange(next);
  }

  _setListening(next) {
    if (!this.target || next === this.listening) return;
    if (next) {
      this.target.addEventListener("scroll", this._onScroll, { passive: true });
    } else {
      this.target.removeEventListener("scroll", this._onScroll);
    }
    this.listening = next;
  }

  _setScrollRoom(next) {
    if (next === this.hasScrollRoom) return;
    this.hasScrollRoom = next;
    this.onScrollRoomChange(next);
  }

  _setScrolled(next) {
    if (next === this.hasScrolled) return;
    this.hasScrolled = next;
    this.onScrolledChange(next);
  }

  _getScrollTop() {
    if (this.target && this.target !== window) {
      return this.target.scrollTop || 0;
    }
    return (
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0
    );
  }

  _getMaxScroll() {
    if (this.target && this.target !== window) {
      return (this.target.scrollHeight || 0) - (this.target.clientHeight || 0);
    }
    return (
      (document.documentElement.scrollHeight || 0) -
      (window.innerHeight || 0)
    );
  }

  _getHideableHeight() {
    if (!this.hideableSelector) return 0;
    const el = document.querySelector(this.hideableSelector);
    return el ? el.offsetHeight || 0 : 0;
  }

  _getViewportHeight() {
    if (this.target && this.target !== window) {
      return this.target.clientHeight || 0;
    }
    return window.innerHeight || 0;
  }

  _isMeasurable() {
    if (!this.target || this.target === window) return true;
    return (this.target.clientHeight || 0) > 0;
  }

  _hasScrollRoom() {
    return this._getMaxScroll() > this.scrollRoomTolerance;
  }

  _isScrollable() {
    const floor = Math.max(
      this.minScrollableHeight,
      this._getHideableHeight(),
      this._getViewportHeight() * this.minScrollableRatio,
    );
    return this._getMaxScroll() > this.topOffset + floor;
  }

  _update() {
    const currentScrollY = this._getScrollTop();
    const diff = currentScrollY - this.lastScrollY;
    this.lastScrollY = currentScrollY > 0 ? currentScrollY : 0;
    this.ticking = false;

    if (!this._isMeasurable()) {
      this._setScrollRoom(false);
      this._setScrollable(false);
      this._setHidden(false, true);
      this._setScrolled(false);
      this._setListening(false);
      return;
    }

    const room = this._hasScrollRoom();
    this._setScrollRoom(room);
    this._setListening(room);

    // If content fits in viewport, don't engage hide-on-scroll —
    // hiding bars on a short list creates a scroll dead-zone.
    if (!this._isScrollable()) {
      this._setScrollable(false);
      this._setHidden(false, true);
      this._setScrolled(false);
      return;
    }

    this._setScrollable(true);

    const maxScroll = this._getMaxScroll();

    // Ignore overscroll bounce
    if (currentScrollY < 0) return;
    if (maxScroll > 0 && currentScrollY > maxScroll - this.bottomOffset) return;

    if (currentScrollY <= this.topOffset) {
      this._setHidden(false);
      this._setScrolled(false);
      return;
    }

    this._setScrolled(true);

    if (Math.abs(diff) < this.threshold) return;

    this._setHidden(diff > 0);
  }

  _onScroll() {
    if (!this.ticking) {
      window.requestAnimationFrame(this._update);
      this.ticking = true;
    }
  }

  _attach() {
    let container =
      typeof this.scrollContainer === "function"
        ? this.scrollContainer()
        : this.scrollContainer;

    if (this.scrollContainer) {
      if (!container) return false;
      this.target = container;
    } else if (this.scrollContainerSelector) {
      this.target = document.querySelector(this.scrollContainerSelector);
    } else {
      this.target = window;
    }

    if (!this.target) return false;

    this.lastScrollY = this._getScrollTop();
    this._setListening(true);
    this._observeSize();
    this._update();
    return true;
  }

  _observeSize() {
    if (typeof ResizeObserver === "undefined") return;
    if (this.target === window) return;

    this.resizeObserver = new ResizeObserver(() => {
      if (this.running) this._update();
    });
    this._observeChildren();

    if (typeof MutationObserver === "undefined") return;
    this.mutationObserver = new MutationObserver(() => {
      if (!this.running) return;
      this._observeChildren();
      this._onScroll();
    });
    this.mutationObserver.observe(this.target, {
      childList: true,
      subtree: true,
    });
  }

  _observeChildren() {
    if (!this.resizeObserver || !this.target || this.target === window) return;
    this.resizeObserver.observe(this.target);
    Array.prototype.forEach.call(this.target.children, (child) => {
      this.resizeObserver.observe(child);
    });
  }

  _detach() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
    if (this.rafId) {
      window.cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.target) {
      this._setListening(false);
      this.target = null;
    }
  }

  _tryAttach() {
    const MAX_ATTEMPTS = 30;
    if (!this.running) return;
    if (this._attach()) {
      this.attachAttempts = 0;
      return;
    }
    this.attachAttempts += 1;
    if (this.attachAttempts < MAX_ATTEMPTS) {
      this.rafId = window.requestAnimationFrame(this._tryAttach);
    }
  }
}

export default ScrollDirectionTracker;